import React, { useState, useEffect } from 'react';
import styles from './Banner.module.css';
import BackgroundShadow from '../shared/BackgroundShadow';
import {
    OptimizedMotionH1,
    OptimizedMotionH2,
    OptimizedMotionP,
} from '../../../shared/ui/optimized-motion/OptimizedMotion';
import { axiosInstance } from '../../../shared/api/axiosInstance';
import { toast } from 'react-toastify';

const Banner: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        linkedin: '',
        company: '',
        message: '',
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [errors, setErrors] = useState({
        name: '',
        email: '',
        linkedin: '',
        message: '',
    });

    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, []);

    const validateField = (name: string, value: string): string => {
        switch (name) {
            case 'name':
                return value.length < 2 ? 'Name must be at least 2 characters.' : '';
            case 'email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return !emailRegex.test(value) ? 'Please enter a valid email address.' : '';
            case 'linkedin':
                if (!value) return '';
                const linkedinRegex =
                    /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/[A-Za-z0-9-_]+\/?$/;
                return !linkedinRegex.test(value)
                    ? 'Please enter a valid LinkedIn profile or company URL.'
                    : '';
            case 'message':
                return value.length < 10 ? 'Message must be at least 10 characters.' : '';
            default:
                return '';
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (isSuccess) {
            setIsSuccess(false);
        }

        const errorMessage = validateField(name, value);

        setErrors((prev) => ({
            ...prev,
            [name]: errorMessage,
        }));
    };

    const validateForm = (): boolean => {
        const newErrors = {
            name: validateField('name', formData.name),
            email: validateField('email', formData.email),
            linkedin: validateField('linkedin', formData.linkedin),
            message: validateField('message', formData.message),
        };

        setErrors(newErrors);
        return Object.values(newErrors).every((error) => error === '');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);
        setIsSuccess(false);

        try {
            const contactData = {
                data: {
                    type: 'contact-form',
                    attributes: {
                        name: formData.name,
                        email: formData.email,
                        linkedin: formData.linkedin,
                        company: formData.company,
                        message: formData.message,
                    },
                },
            };

            await axiosInstance.post('/contact', contactData);

            setIsSuccess(true);

            setFormData({
                name: '',
                email: '',
                linkedin: '',
                company: '',
                message: '',
            });

            setErrors({
                name: '',
                email: '',
                linkedin: '',
                message: '',
            });
        } catch (error: any) {
            console.error('Error submitting form:', error);

            const apiErrors = error?.response?.data?.errors;

            const newErrors: any = {
                name: '',
                email: '',
                linkedin: '',
                message: '',
            };

            if (Array.isArray(apiErrors)) {
                const fieldErrors = apiErrors[0]?.meta?.errors;

                if (fieldErrors) {
                    Object.keys(fieldErrors).forEach((key) => {
                        const cleanKey = key.replace('data.attributes.', '');
                        if (newErrors[cleanKey] !== undefined) {
                            newErrors[cleanKey] = fieldErrors[key][0];
                        }
                    });

                    setErrors(newErrors);
                    return;
                }
            }

            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.contactContainer}>
                <div className={styles.leftContainer}>
                    <OptimizedMotionH2 className={styles.title} type="slide-left" delay={0.4}>
                        <span className={styles.titleHighlight}>Get</span> in touch!
                    </OptimizedMotionH2>

                    <p className={styles.description}>
                        Whether you're curious about how SimpleeCreate can supercharge your content
                        ops, need support, or want to explore a partnership — our team is ready to
                        help.
                    </p>
                </div>

                {!isSuccess ? (
                    <div className={styles.formContainer}>
                        <h3 className={styles.formTitle}>Contact form</h3>

                        <form onSubmit={handleSubmit} className={styles.form}>
                            {/* Full name + Email */}
                            <div className={styles.inputRow}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>Full name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Enter your first and last name"
                                        className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {errors.name && (
                                        <span className={styles.errorMessage}>{errors.name}</span>
                                    )}
                                </div>

                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Enter your email"
                                        className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {errors.email && (
                                        <span className={styles.errorMessage}>{errors.email}</span>
                                    )}
                                </div>
                            </div>

                            {/* LinkedIn + Company */}
                            <div className={styles.inputRow}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>LinkedIn URL (Optional)</label>
                                    <input
                                        type="text"
                                        name="linkedin"
                                        placeholder="Enter your LinkedIn handle"
                                        className={styles.input}
                                        value={formData.linkedin}
                                        onChange={handleInputChange}
                                    />
                                    {errors.linkedin && (
                                        <span className={styles.errorMessage}>
                                            {errors.linkedin}
                                        </span>
                                    )}
                                </div>

                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>Company name</label>
                                    <input
                                        type="text"
                                        name="company"
                                        placeholder="Enter your company name"
                                        className={styles.input}
                                        value={formData.company}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            {/* Message */}
                            <div className={styles.inputGroup}>
                                <label className={styles.label}>Message</label>
                                <textarea
                                    name="message"
                                    placeholder="Enter your message"
                                    className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    rows={4}
                                    required
                                />
                                {errors.message && (
                                    <span className={styles.errorMessage}>{errors.message}</span>
                                )}
                            </div>

                            <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                                {isLoading ? (
                                    <div className={styles.spinnerContainer}>
                                        <div className={styles.spinner}></div>
                                        Sending...
                                    </div>
                                ) : (
                                    'Submit'
                                )}
                            </button>
                        </form>
                    </div>
                ) : (
                    <div className={styles.successContainer}>
                        <div className={styles.successMessage}>
                            <h2 className={styles.successTitle}>Message Sent Successfully!</h2>
                            <p className={styles.successDescription}>
                                Thank you for reaching out to us. We'll get back to you soon!
                            </p>
                        </div>

                        <button onClick={() => setIsSuccess(false)} className={styles.submitButton}>
                            Submit Another
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Banner;
